import { useState, useEffect, useRef, DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { useAuth } from '@/hooks/useAuth';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { useTimezone } from '@/contexts/TimezoneContext';
import { useTheme } from '@/hooks/useTheme';
import { useLanguage } from '@/hooks/useLanguage';
import { Icon } from '@/components/ui/Icon';
import { RetryImage } from '@/components/ui';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { profileService, ProfileStats, ActivityItem, ActivityResponse } from '@/services/profile.service';
import { mediaService } from '@/services/media.service';
import { TimezoneSelect } from '@/components/common/TimezoneSelect';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { teamApi, Team } from '@/services/team';
import { devLog } from '@/utils/logger';

/**
 * Profile Page — v2 redesign
 *
 * Hero card with banner, profile completion meter, stats row, quick actions grid,
 * identity card with live clock, vertical activity timeline, enhanced edit modal.
 */

// ─── Helpers ────────────────────────────────────────────────────────────────

const getInitials = (name: string): string =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

const getUserAvatar = (preferences?: Record<string, unknown> | null): string | null => {
  const prefs = preferences as { avatar?: string } | null;
  return prefs?.avatar || null;
};

const getRoleBadgeStyle = (role: string | undefined | null): { bg: string; text: string; border: string } => {
  switch (role) {
    case 'super_admin':
      return { bg: 'bg-dark-teal', text: 'text-white', border: 'border-dark-teal' };
    case 'owner':
      return { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' };
    case 'admin':
      return { bg: 'bg-coral-100', text: 'text-coral-700', border: 'border-coral-200' };
    case 'member':
    case 'reviewer':
    default:
      return { bg: 'bg-primary-100', text: 'text-primary-700', border: 'border-primary-200' };
  }
};

/** Get time-of-day greeting key based on hour in user's timezone */
const getGreetingKey = (timezone: string | null | undefined): 'good_morning' | 'good_afternoon' | 'good_evening' => {
  let hour = new Date().getHours();
  if (timezone) {
    try {
      const timeStr = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: 'numeric',
        hour12: false,
      }).format(new Date());
      hour = parseInt(timeStr, 10);
    } catch {
      // fallback to local hour
    }
  }
  if (hour >= 5 && hour < 12) return 'good_morning';
  if (hour >= 12 && hour < 18) return 'good_afternoon';
  return 'good_evening';
};

/** Activity type dot color */
const getActivityDotColor = (type: ActivityItem['type']): string => {
  switch (type) {
    case 'post_created':
      return 'bg-primary-500';
    case 'post_published':
      return 'bg-green-500';
    case 'post_failed':
      return 'bg-red-500';
    // Task #182 reconciliation (POST-STATUS-MODEL.md): Partial/Missed are
    // first-class, distinct from a genuine Failed — never red.
    case 'post_partial':
      return 'bg-amber-500';
    case 'post_missed':
      return 'bg-gray-400';
    case 'channel_connected':
      return 'bg-coral-500';
    default: {
      // Exhaustive guard (Task #182 F2): a compile error here means a new
      // `ActivityItem['type']` was added without a dot color — fix it above,
      // don't fall through to a silent default.
      const _exhaustive: never = type;
      return _exhaustive;
    }
  }
};

/**
 * Compose the localized activity message from structured fields (Task #182
 * #199 fix — backend used to pre-bake English `message` strings; now it
 * sends structured `postTitle`/`channelName`/`platform` and the frontend
 * composes via i18n so non-English users see a translated line).
 */
const composeActivityMessage = (item: ActivityItem, t: TFunction): string => {
  switch (item.type) {
    case 'post_created':
      return t('common:activity_post_created', { title: item.postTitle ?? t('common:untitled_post') });
    case 'post_published':
      return t('common:activity_post_published', { title: item.postTitle ?? t('common:untitled_post') });
    case 'post_partial':
      return t('common:activity_post_partial', { title: item.postTitle ?? t('common:untitled_post') });
    case 'post_missed':
      return t('common:activity_post_missed', { title: item.postTitle ?? t('common:untitled_post') });
    case 'post_failed':
      return t('common:activity_post_failed', { title: item.postTitle ?? t('common:untitled_post') });
    case 'channel_connected':
      return t('common:activity_channel_connected', {
        platform: item.platform ?? '',
        channel: item.channelName ?? '',
      });
    default: {
      // Exhaustive guard (Task #182 F2): a compile error here means a new
      // `ActivityItem['type']` was added without a message composer — fix it
      // above, don't fall through to a blank activity line.
      const _exhaustive: never = item.type;
      return _exhaustive;
    }
  }
};

// ─── Sub-components ──────────────────────────────────────────────────────────

interface ProfileCompletionProps {
  percent: number;
}

const ProfileCompletion = ({ percent }: ProfileCompletionProps) => {
  const { t } = useTranslation('common');

  if (percent >= 100) {
    return (
      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 shadow-sm">
        <span className="text-xs font-semibold text-white drop-shadow-sm">{t('profile_complete')} ✓</span>
      </div>
    );
  }

  return (
    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 shadow-sm">
      <span className="text-xs font-medium text-white/90 whitespace-nowrap drop-shadow-sm">
        {t('profile_completion', { percent })}
      </span>
      <div className="w-16 h-1.5 rounded-full bg-white/30 overflow-hidden">
        <div
          className="h-full rounded-full bg-white transition-all duration-500"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
};

interface StatsRowProps {
  stats: ProfileStats | undefined;
  /** Brand Portfolio name for team-less agency staff; replaces the team members tile. */
  portfolioName?: string | null;
}

const StatsRow = ({ stats, portfolioName }: StatsRowProps) => {
  const { t } = useTranslation(['common', 'dashboard']);

  const items = [
    {
      label: t('dashboard:posts_created'),
      value: stats?.postsCreated?.toString() ?? '—',
      iconColor: 'text-primary-500',
      dotBg: 'bg-primary-100',
    },
    {
      label: t('dashboard:posts_published'),
      value: stats?.postsPublished?.toString() ?? '—',
      iconColor: 'text-green-500',
      dotBg: 'bg-green-100',
    },
    {
      label: t('dashboard:channels_connected'),
      value: stats?.channelsConnected?.toString() ?? '—',
      iconColor: 'text-coral-500',
      dotBg: 'bg-coral-100',
    },
    {
      label: portfolioName ? t('common:profile_brand_portfolio') : t('dashboard:team_members'),
      value: portfolioName ?? stats?.teamMembers?.toString() ?? '—',
      iconColor: 'text-amber-500',
      dotBg: 'bg-amber-100',
    },
  ] as const;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
      {items.map((item, i) => (
        <div key={i} className="flex flex-col items-center py-4 px-2 gap-0.5">
          <span className="text-2xl font-bold text-gray-900 leading-tight max-w-full truncate" title={item.value}>{item.value}</span>
          <span className="text-xs text-gray-500 text-center leading-snug">{item.label}</span>
        </div>
      ))}
    </div>
  );
};

interface QuickActionTileProps {
  icon: string;
  label: string;
  description: string;
  bgClass: string;
  onClick: () => void;
}

const QuickActionTile = ({ icon, label, description, bgClass, onClick }: QuickActionTileProps) => (
  <button
    onClick={onClick}
    className={cn(
      'relative group flex flex-col items-start gap-2 p-4 sm:p-5 rounded-2xl text-left aspect-square',
      'hover:-translate-y-0.5 hover:shadow-md transition-all duration-150',
      bgClass
    )}
  >
    <Icon name={icon} size="md" color="text-gray-700" ariaHidden />
    <div className="flex-1">
      <p className="text-sm font-semibold text-gray-900 leading-snug">{label}</p>
      <p className="text-xs text-gray-500 mt-0.5 leading-snug">{description}</p>
    </div>
    <span className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity">
      <Icon name={ICONS.ANGLE_RIGHT} size="xs" color="text-gray-500" ariaHidden />
    </span>
  </button>
);

interface IdentityRowProps {
  icon: string;
  label: string;
  value: string;
  suffix?: string;
  onClick?: () => void;
}

const IdentityRow = ({ icon, label, value, suffix, onClick }: IdentityRowProps) => {
  const { t } = useTranslation('common');
  const inner = (
    <>
      <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center">
        <Icon name={icon} size="xs" color="text-gray-500" ariaHidden />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs uppercase tracking-wide text-gray-400 font-medium leading-none mb-0.5">{label}</p>
        <p className="text-sm font-medium text-gray-900 truncate">{value}</p>
      </div>
      {suffix && (
        <span className="flex-shrink-0 text-xs text-gray-400 font-mono">{suffix}</span>
      )}
      {onClick && (
        <span className="flex-shrink-0 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity">
          <Icon name={ICONS.EDIT} size="xs" color="text-gray-300" ariaHidden />
        </span>
      )}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={`${t('edit')}: ${label}`}
        className="w-full text-left flex items-center gap-3 py-3 group -mx-2 px-2 rounded-lg hover:bg-gray-50 transition-colors"
      >
        {inner}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-3 py-3 group">
      {inner}
    </div>
  );
};

interface ActivityDayGroupProps {
  label: string;
  items: ActivityItem[];
  isLast: boolean;
  formatDateTime: (date: string | Date | null | undefined) => string;
  t: TFunction;
}

const ActivityDayGroup = ({ label, items, isLast, formatDateTime, t }: ActivityDayGroupProps) => (
  <div>
    {/* Day pill header */}
    <div className="flex items-center gap-3 mb-3">
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-gray-100 text-xs font-medium text-gray-500">
        {label}
      </span>
      <div className="flex-1 h-px bg-gray-100" />
    </div>

    {/* Items with timeline line */}
    <div className={cn('relative pl-5', !isLast && 'mb-6')}>
      {/* Vertical line */}
      <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-gray-200" />

      <div className="space-y-4">
        {items.map((item, index) => (
          <div key={`${item.id}-${index}`} className="relative flex items-start gap-3">
            {/* Dot on the line */}
            <div
              className={cn(
                'absolute -left-[22px] top-1.5 w-3 h-3 rounded-full border-2 border-white shadow-sm flex-shrink-0',
                getActivityDotColor(item.type)
              )}
            />
            {/* Content */}
            <div className="flex-1 min-w-0 ml-1">
              <p className="text-sm text-gray-800 leading-snug">{composeActivityMessage(item, t)}</p>
              <p className="text-xs text-gray-400 mt-0.5">{formatDateTime(item.timestamp)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ─── Live clock hook ──────────────────────────────────────────────────────────

const useLiveClock = (timezone: string | null | undefined): string => {
  const [time, setTime] = useState<string>('');

  useEffect(() => {
    const update = () => {
      if (!timezone) {
        setTime('');
        return;
      }
      try {
        const formatted = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }).format(new Date());
        setTime(formatted);
      } catch {
        setTime('');
      }
    };

    update();
    const id = setInterval(update, 60_000);
    return () => clearInterval(id);
  }, [timezone]);

  return time;
};

// ─── Activity grouping helpers ────────────────────────────────────────────────

type DayGroup = { label: string; items: ActivityItem[] };

/** Returns a YYYY-MM-DD string for a date in the given IANA timezone */
const getDateStrInTz = (d: Date, tz: string): string => {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  } catch {
    // Fallback to ISO date if timezone is invalid
    return d.toISOString().slice(0, 10);
  }
};

const groupActivitiesByDay = (
  items: ActivityItem[],
  todayLabel: string,
  yesterdayLabel: string,
  earlierLabel: string,
  formatDateOnly: (date: string | Date | null | undefined) => string,
  userTimezone: string
): DayGroup[] => {
  const groups: Map<string, DayGroup> = new Map();

  const now = new Date();
  const todayStr = getDateStrInTz(now, userTimezone);
  const yesterdayMs = now.getTime() - 86_400_000;
  const yesterdayStr = getDateStrInTz(new Date(yesterdayMs), userTimezone);

  for (const item of items) {
    const d = new Date(item.timestamp);
    const dStr = getDateStrInTz(d, userTimezone);

    let groupKey: string;
    let groupLabel: string;

    if (dStr === todayStr) {
      groupKey = '__today__';
      groupLabel = todayLabel;
    } else if (dStr === yesterdayStr) {
      groupKey = '__yesterday__';
      groupLabel = yesterdayLabel;
    } else {
      const formatted = formatDateOnly(item.timestamp);
      groupKey = formatted;
      groupLabel = formatted;
    }

    if (!groups.has(groupKey)) {
      groups.set(groupKey, { label: groupLabel, items: [] });
    }
    groups.get(groupKey)!.items.push(item);
  }

  // Collapse everything older than yesterday into "Earlier" if multiple sparse groups
  const result: DayGroup[] = [];
  const earlierItems: ActivityItem[] = [];

  for (const [key, group] of groups.entries()) {
    if (key === '__today__' || key === '__yesterday__') {
      result.push(group);
    } else {
      earlierItems.push(...group.items);
    }
  }

  if (earlierItems.length > 0) {
    result.push({ label: earlierLabel, items: earlierItems });
  }

  return result;
};

// ─── Main Component ───────────────────────────────────────────────────────────

export const Profile = () => {
  const { t } = useTranslation(['common', 'settings', 'dashboard']);
  const { user, refreshUser } = useAuth();
  const { isAgencyStaff, hasTeam } = useEffectiveTeamId();
  const { formatDateOnly, formatDateTime } = useTimezone();
  const { currentLanguage, languages } = useLanguage();
  const { branding } = useTheme();
  const navigate = useNavigate();

  const [showEditModal, setShowEditModal] = useState(false);
  const [editName, setEditName] = useState('');
  const [editTimezone, setEditTimezone] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [activityPage, setActivityPage] = useState(1);
  const [allActivities, setAllActivities] = useState<ActivityItem[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const liveTime = useLiveClock(user?.timezone);

  // Dirty only while the edit-profile modal is open and a field diverges
  // from the currently-saved user data.
  const isProfileDirty =
    showEditModal &&
    (editName !== (user?.fullName ?? '') ||
      editTimezone !== (user?.timezone ?? '') ||
      avatarFile !== null);
  useUnsavedGuard(isProfileDirty);

  // ── Page title registration (replaces PageHeader) ─────────────────────────
  const pageTitle = usePageTitle();
  const profileTitle = t('common:my_profile');
  useDocumentTitle(profileTitle);
  useEffect(() => {
    pageTitle.setPageTitle(profileTitle, ICONS.USER);
    return () => {
      pageTitle.setPageTitle('');
      pageTitle.setMinimized(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileTitle]);

  if (!user) {
    return null;
  }

  // Team-less agency staff (no home team, not in God Mode): show the Brand
  // Portfolio role instead of a team role / team stats.
  const portfolioName: string | null =
    isAgencyStaff && !hasTeam && !user.team ? (user.agency?.name ?? null) : null;
  const isPortfolioOnly = portfolioName !== null;
  const effectiveRole: string = user.isSuperAdmin
    ? 'super_admin'
    : isPortfolioOnly && user.agencyRole
      ? user.agencyRole.toLowerCase()
      : (user.teamRole?.toLowerCase() ?? 'member');
  const roleStyle = getRoleBadgeStyle(effectiveRole);

  // Profile completion: 33% per step (fullName, avatar, timezone)
  const completionSteps = [
    Boolean(user.fullName && user.fullName.trim().length > 0),
    Boolean(getUserAvatar(user.preferences)),
    Boolean(user.timezone),
  ];
  const completionPercent = Math.round((completionSteps.filter(Boolean).length / completionSteps.length) * 100);

  // ── Queries ──────────────────────────────────────────────────────────────

  const { data: stats } = useQuery<ProfileStats>({
    queryKey: ['profile', 'stats'],
    queryFn: profileService.getStats,
  });

  const { data: teamData } = useQuery<Team>({
    queryKey: ['team'],
    queryFn: teamApi.getTeam,
    enabled: !!user?.teamId,
    staleTime: 5 * 60 * 1000,
  });

  const { data: activityData, isLoading: isLoadingActivity } = useQuery<ActivityResponse>({
    queryKey: ['profile', 'activity', activityPage],
    queryFn: () => profileService.getActivity(activityPage, 5),
  });

  // Accumulate activities when page changes
  useEffect(() => {
    if (!activityData?.items) return;
    if (activityData.pagination.page === 1) {
      setAllActivities(activityData.items);
    } else {
      setAllActivities((prev) => [...prev, ...activityData.items]);
    }
  }, [activityData]);

  // ── Mutation ──────────────────────────────────────────────────────────────

  const updateProfileMutation = useMutation({
    mutationFn: profileService.updateProfile,
    onSuccess: async () => {
      await refreshUser();
      setShowEditModal(false);
    },
  });

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (avatarPreview) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleFileSelect = (file: File) => {
    setAvatarFile(file);
    setUploadError(null);
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleFileSelect(file);
    }
  };

  const handleRemoveAvatar = () => {
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarFile(null);
    setAvatarPreview(null);
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    let avatarUrl: string | undefined;

    if (avatarFile) {
      try {
        setIsUploadingAvatar(true);
        setUploadError(null);
        // Avatars are never attached to a post (no PostMedia row), so they
        // never count toward the team's storage quota — skip the check.
        const upload = await mediaService.directUpload(avatarFile, { skipStorageCheck: true });
        avatarUrl = upload.optimizedUrl || upload.originalUrl;
      } catch (error) {
        devLog.error('Failed to upload avatar:', error);
        setIsUploadingAvatar(false);
        setUploadError(t('common:avatar_upload_failed'));
        return;
      } finally {
        setIsUploadingAvatar(false);
      }
    }

    updateProfileMutation.mutate({
      fullName: editName,
      timezone: editTimezone || null,
      ...(avatarUrl && { avatar: avatarUrl }),
    });
  };

  // ── Data ──────────────────────────────────────────────────────────────────

  const quickActions = [
    { icon: ICONS.SETTINGS, label: t('settings:account_settings'), description: t('settings:manage_account'), path: '/settings?tab=account', bgClass: 'bg-gradient-mint' },
    { icon: ICONS.SHARE, label: t('settings:connected_channels'), description: t('settings:manage_social_accounts'), path: '/settings?tab=channels', bgClass: 'bg-gradient-coral' },
    { icon: ICONS.CALENDAR, label: t('settings:my_calendars'), description: t('settings:view_calendars'), path: '/settings?tab=calendars', bgClass: 'bg-gradient-peach' },
    { icon: ICONS.BELL, label: t('settings:notifications'), description: t('settings:notification_preferences'), path: '/settings?tab=notifications', bgClass: 'bg-primary-25' },
  ];

  const dayGroups = groupActivitiesByDay(
    allActivities,
    t('common:today'),
    t('common:yesterday'),
    t('common:earlier'),
    formatDateOnly,
    user.timezone || user.team?.timezone || 'Europe/Berlin'
  );

  const currentAvatar = getUserAvatar(user.preferences);
  const greetingKey = getGreetingKey(user.timezone);
  const firstName = user.fullName.split(' ')[0] || user.email.split('@')[0];
  const currentLangName = languages.find((l) => l.code === currentLanguage)?.nativeName || 'English';

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-6">

      {/* ── Hero Card ─────────────────────────────────────────────────────── */}
      <div className="rounded-3xl border border-gray-200 bg-white shadow-sm overflow-hidden animate-slide-in-from-bottom">

        {/* Banner layer — serves as page header + greeting */}
        <div
          className="relative h-[160px] sm:h-[200px]"
          style={
            branding?.assets?.dashboardHeader
              ? {
                  backgroundImage: `url(${branding.assets.dashboardHeader})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }
              : undefined
          }
        >
          {/* Gradient fallback when no header image */}
          {!branding?.assets?.dashboardHeader && (
            <div className="absolute inset-0 bg-gradient-to-br from-primary-600 via-primary-700 to-secondary-600" />
          )}

          {/* Dark overlay for header images — improves text contrast */}
          {branding?.assets?.dashboardHeader && (
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/40 to-black/20" />
          )}

          {/* All banner content sits above the overlay */}
          <div className="relative z-10 h-full">

            {/* Profile completion pill — top left */}
            <div className="absolute top-4 left-4">
              <ProfileCompletion percent={completionPercent} />
            </div>

            {/* Edit profile button — top right */}
            <button
              onClick={() => {
                setEditName(user.fullName);
                setEditTimezone(user.timezone || '');
                setAvatarFile(null);
                setAvatarPreview(null);
                setShowEditModal(true);
              }}
              className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/20 backdrop-blur-sm border border-white/30 shadow-sm text-xs font-medium text-white hover:bg-white/30 transition-all"
            >
              <Icon name={ICONS.EDIT} size="xs" ariaHidden />
              {t('common:edit_profile')}
            </button>

            {/* Page title + greeting — centered in banner, non-interactive so it doesn't block the edit button */}
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center px-4">
              <h1 className="font-wagner font-bold text-white drop-shadow-lg text-3xl sm:text-4xl md:text-5xl leading-tight">
                {t('common:my_profile')}
              </h1>
              <p className="mt-1 sm:mt-2 text-sm sm:text-base md:text-lg text-white/90 drop-shadow-md leading-snug">
                {t(`common:${greetingKey}`, { name: firstName })}
              </p>
            </div>

          </div>{/* end relative z-10 */}

          {/* Avatar — overlapping banner bottom */}
          <div className="absolute -bottom-12 left-6 sm:left-8">
            <div className="relative">
              {currentAvatar ? (
                <RetryImage
                  src={currentAvatar}
                  alt={user.fullName}
                  className="w-24 h-24 rounded-2xl object-cover ring-4 ring-white shadow-md"
                />
              ) : (
                <div className="w-24 h-24 rounded-2xl bg-primary-400 flex items-center justify-center text-primary-400-contrast text-3xl font-bold ring-4 ring-white shadow-md">
                  {getInitials(user.fullName)}
                </div>
              )}
              {/* Online dot */}
              <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-green-500 border-2 border-white shadow-sm" />
            </div>
          </div>
        </div>

        {/* Content layer */}
        <div className="pt-16 pb-0 px-6 sm:px-8">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">

            {/* Name + email + badges */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900">{user.fullName}</h2>
              <p className="text-gray-500 mt-1 flex items-center gap-1.5">
                <Icon name={ICONS.MAIL} size="xs" color="text-gray-400" ariaHidden />
                <span className="text-sm">{user.email}</span>
              </p>

              <div className="mt-3 flex flex-wrap gap-2">
                <span className={cn(
                  'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border',
                  roleStyle.bg, roleStyle.text, roleStyle.border
                )}>
                  <Icon name={ICONS.SHIELD} size="xs" ariaHidden />
                  {t(`common:role_${effectiveRole}`)}
                </span>
                {(user.team || portfolioName) && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                    <Icon name={ICONS.USERS} size="xs" ariaHidden />
                    {user.team?.name ?? portfolioName}
                  </span>
                )}
                {user.createdAt && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                    <Icon name={ICONS.CALENDAR} size="xs" ariaHidden />
                    {t('common:member_since', { date: formatDateOnly(user.createdAt) })}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className="mt-6 border-t border-gray-100">
          <StatsRow stats={stats} portfolioName={portfolioName} />
        </div>
      </div>

      {/* ── Quick Actions + Identity ──────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 order-3 sm:order-2">

        {/* Quick Actions — colorful 2×2 tile grid */}
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
          <h3 className="text-base font-semibold text-dark-teal mb-4 flex items-center gap-2">
            <Icon name={ICONS.SPARK} size="sm" color="text-primary-500" ariaHidden />
            {t('common:quick_actions')}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {quickActions.map((action, index) => (
              <QuickActionTile
                key={index}
                icon={action.icon}
                label={action.label}
                description={action.description}
                bgClass={action.bgClass}
                onClick={() => navigate(action.path)}
              />
            ))}
          </div>
        </div>

        {/* Identity Card — stacked rows with live clock */}
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
          <h3 className="text-base font-semibold text-dark-teal mb-2 flex items-center gap-2">
            <Icon name={ICONS.INFO} size="sm" color="text-primary-500" ariaHidden />
            {t('common:identity')}
          </h3>
          <div className="divide-y divide-gray-100">
            <IdentityRow
              icon={ICONS.USER}
              label={t('common:fullname')}
              value={user.fullName}
              onClick={() => setShowEditModal(true)}
            />
            <IdentityRow
              icon={ICONS.MAIL}
              label={t('common:email')}
              value={user.email}
            />
            <IdentityRow
              icon={ICONS.USERS}
              label={isPortfolioOnly ? t('common:profile_brand_portfolio') : t('settings:my_team')}
              value={
                portfolioName ??
                (user.currentTeam?.name || user.team?.name || t('common:no_team_assigned'))
              }
            />
            <IdentityRow
              icon={ICONS.GLOBE}
              label={t('settings:timezone')}
              value={user.timezone || t('common:timezone_not_set')}
              suffix={liveTime || undefined}
              onClick={() => setShowEditModal(true)}
            />
            <IdentityRow
              icon={ICONS.LANGUAGE}
              label={t('common:ui_lang')}
              value={currentLangName}
              onClick={() => setShowEditModal(true)}
            />
          </div>
        </div>
      </div>

      {/* ── Activity Timeline ─────────────────────────────────────────────── */}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm order-2 sm:order-3">
        <div className="p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-base font-semibold text-dark-teal flex items-center gap-2">
              <Icon name={ICONS.CLOCK} size="sm" color="text-amber-500" ariaHidden />
              {t('common:activity_timeline')}
            </h3>
            {activityData?.pagination?.total !== undefined && (
              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                {activityData.pagination.total} {t('common:total')}
              </span>
            )}
          </div>

          {allActivities.length > 0 ? (
            <div>
              {dayGroups.map((group, gi) => (
                <ActivityDayGroup
                  key={group.label}
                  label={group.label}
                  items={group.items}
                  isLast={gi === dayGroups.length - 1}
                  formatDateTime={formatDateTime}
                  t={t}
                />
              ))}

              {/* Load More */}
              {activityData?.pagination?.hasMore && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => setActivityPage((prev) => prev + 1)}
                    disabled={isLoadingActivity}
                    className="w-full py-2.5 text-sm font-medium text-gray-500 hover:text-gray-700 rounded-xl border border-gray-200 hover:border-gray-300 bg-white hover:bg-gray-50 transition-all disabled:opacity-50"
                  >
                    {isLoadingActivity ? t('common:loading') : t('common:load_more')}
                  </button>
                </div>
              )}
            </div>
          ) : isLoadingActivity ? (
            <div className="text-center py-10">
              <p className="text-sm text-gray-400">{t('common:loading_activity')}</p>
            </div>
          ) : (
            <div className="text-center py-10">
              <div className="mx-auto mb-3 w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
                <Icon name={ICONS.CLOCK} size="lg" color="text-gray-300" ariaHidden />
              </div>
              <p className="text-sm text-gray-400">{t('common:no_recent_activity')}</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Edit Profile Modal ────────────────────────────────────────────── */}
      {showEditModal && (
        <div className="fixed inset-0 z-modal-backdrop flex items-center justify-center bg-black/50 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-profile-modal-title"
            className="bg-white rounded-2xl shadow-xl w-full max-w-lg animate-slide-in-from-bottom"
          >

            {/* Modal header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h3 id="edit-profile-modal-title" className="text-lg font-semibold text-gray-900">{t('common:edit_profile')}</h3>
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
                aria-label={t('common:cancel')}
              >
                <Icon name={ICONS.CLOSE} size="sm" color="text-gray-500" ariaHidden />
              </button>
            </div>

            <form onSubmit={handleFormSubmit} className="p-6 space-y-5">

              {/* Avatar drop zone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common:profile_photo')}
                </label>

                {avatarPreview || currentAvatar ? (
                  /* Preview with change/remove */
                  <div className="flex flex-col items-center gap-3">
                    <div className="relative">
                      <RetryImage
                        src={avatarPreview || currentAvatar!}
                        alt={t('common:avatar_preview', { defaultValue: 'Avatar preview' })}
                        className="w-28 h-28 rounded-2xl object-cover border-2 border-gray-200 shadow-sm"
                      />
                    </div>
                    <div className="flex gap-2">
                      <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                        <Icon name={ICONS.UPLOAD} size="xs" ariaHidden />
                        {t('common:choose_photo')}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileSelect(file);
                          }}
                        />
                      </label>
                      {(avatarPreview || currentAvatar) && (
                        <button
                          type="button"
                          onClick={handleRemoveAvatar}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                        >
                          <Icon name={ICONS.CROSS} size="xs" ariaHidden />
                          {t('common:remove_photo')}
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Drop zone */
                  <div
                    role="button"
                    tabIndex={0}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
                    className={cn(
                      'relative flex flex-col items-center justify-center gap-2 p-8 rounded-2xl border-2 border-dashed transition-all cursor-pointer',
                      isDragOver
                        ? 'border-primary-400 bg-primary-25 scale-[1.01]'
                        : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100'
                    )}
                  >
                    <div className="w-12 h-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center shadow-sm">
                      <Icon name={ICONS.UPLOAD} size="md" color="text-gray-400" ariaHidden />
                    </div>
                    <p className="text-sm text-gray-600 font-medium text-center">{t('common:drag_drop_photo')}</p>
                    <p className="text-xs text-gray-400">{t('common:photo_requirements')}</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileSelect(file);
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Full name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {t('common:fullname')}
                </label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm transition-shadow"
                  placeholder={t('common:enter_name_placeholder')}
                />
              </div>

              {/* Timezone + Language — side by side on sm+ */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <TimezoneSelect
                    value={editTimezone}
                    onChange={setEditTimezone}
                    label={t('settings:timezone')}
                    placeholder={t('common:select_timezone_placeholder')}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {teamData?.timezone
                      ? t('common:timezone_team_fallback', { timezone: teamData.timezone })
                      : t('common:timezone_default_fallback')}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {t('common:ui_lang')}
                  </label>
                  <LanguageSwitcher className="w-full" />
                  <p className="text-xs text-gray-400 mt-1">{t('common:choose_interface_language')}</p>
                </div>
              </div>

              {/* Upload error */}
              {uploadError && (
                <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-2">
                  {uploadError}
                </p>
              )}

              {/* Profile update error */}
              {updateProfileMutation.isError && (
                <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-2">
                  {(updateProfileMutation.error instanceof Error
                    ? updateProfileMutation.error.message
                    : null) || t('common:profile_update_failed')}
                </p>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-sm text-gray-600 font-medium hover:bg-gray-100 rounded-xl transition-colors"
                >
                  {t('common:cancel')}
                </button>
                <button
                  type="submit"
                  disabled={updateProfileMutation.isPending || isUploadingAvatar}
                  className="px-5 py-2 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-800 transition-colors disabled:opacity-50 shadow-sm"
                >
                  {isUploadingAvatar
                    ? t('common:uploading')
                    : updateProfileMutation.isPending
                    ? t('common:saving')
                    : t('common:save_changes')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
